import { useEffect } from 'react'
import Check from '@mui/icons-material/Check'
import Close from '@mui/icons-material/Close'
import HowToRegOutlined from '@mui/icons-material/HowToRegOutlined'
import WarningAmber from '@mui/icons-material/WarningAmber'
import {
  alpha,
  Box,
  Button,
  Chip,
  IconButton,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material'
import useOverlay from '@/hooks/useOverlay'
import {
  ApprovalAction,
  ApprovalActionType,
  ApprovalOrder,
  BlockedSlot,
  PositionHolder,
} from '@/types/approvals'
import { AuthUser } from '@/types/users'
import { ApprovalEligibility } from '@/utils/approval-actions'
import { blockedSlotCopy } from '@/utils/blocked-slots'
import { holdersLabel } from '@/utils/position-holders'
import { prettifyDate } from '@/utils/time'
import PositionAvatar from '@/components/avatars/PositionAvatar'
import UserAvatar from '@/components/avatars/UserAvatar'
import ProxyActionDialog from './ProxyActionDialog'
import RejectReasonForm from './RejectReasonForm'
import useRejectReasonForm from './useRejectReasonForm'

type ApprovalApproverRowProps = {
  /**
   * The approver's decision, or null for one they haven't made yet. Undefined
   * where the document doesn't have the policy applied — the row then reports
   * no status at all.
   */
  latestAction?: ApprovalAction | null
  /**
   * Set when this entry is a position rather than a person. Whoever holds it
   * approves, so there is no user to show and nobody to call "you".
   */
  positionName?: string
  /** Set for a position slot row: what it resolved from, ready to show. */
  startingPoints?: string[]
  /** Set for a filled position slot row: who the ERP reports as currently holding it. */
  holders?: PositionHolder[]
  /** Set for a position slot row that couldn't be filled — rendered instead of a normal approver row. */
  blocked?: BlockedSlot
  /** The approver's user. Absent while the users query loads, or if they've left the account. */
  user?: AuthUser
  isUserPending: boolean
  /** The position catalog is still loading, so this row has no name yet. */
  isPositionPending?: boolean
  isViewer: boolean
  /** Null when the policy's config couldn't be read. */
  approvalOrder: ApprovalOrder | null
  /** 1-based position within the policy's approver list — only meaningful for sequential policies. */
  stepNumber: number
  /** Sequential only: this row's entry is the policy's `next_required`. */
  isCurrentTurn: boolean
  /** Sequential only: this approver's turn hasn't come up yet. */
  isFutureStep: boolean
  /** Any-order only: the policy is already satisfied and this approver never acted. */
  notNeeded: boolean
  /** Whether the viewer may approve/reject this policy right now. Ignored unless `isViewer`. */
  eligibility?: ApprovalEligibility
  /** A write for this policy is in flight. */
  isActionPending?: boolean
  onApprove?: () => void
  onReject?: (reason: string | null) => void
  /** Lets the parent card highlight itself while this row's reject form is open. */
  onRejectFormOpenChange?: (isOpen: boolean) => void
  /** The viewer may approve or reject for this row's approver. Ignored on the viewer's own row. */
  canActOnBehalf?: boolean
  onApproveOnBehalf?: (reason: string) => void
  onRejectOnBehalf?: (reason: string) => void
  /** Who took this row's latest action, when someone took it on the approver's behalf. */
  actedByUser?: AuthUser
}

function StatusIcon({
  latestAction,
  approvalOrder,
  stepNumber,
  isCurrentTurn,
  isFutureStep,
}: Pick<
  ApprovalApproverRowProps,
  | 'latestAction'
  | 'approvalOrder'
  | 'stepNumber'
  | 'isCurrentTurn'
  | 'isFutureStep'
>) {
  const circleSx = {
    width: 24,
    height: 24,
    borderRadius: '50%',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 'none',
  }

  // A policy the document hasn't applied has no decision outstanding and no
  // turn to be waiting on.
  if (latestAction === undefined) return null

  if (latestAction?.type === 'approval') {
    return (
      <Box sx={{ ...circleSx, bgcolor: 'success.main' }}>
        <Check sx={{ fontSize: 16, color: 'success.contrastText' }} />
      </Box>
    )
  }

  if (latestAction?.type === 'rejection') {
    return (
      <Box sx={{ ...circleSx, bgcolor: 'error.main' }}>
        <Close sx={{ fontSize: 16, color: 'error.contrastText' }} />
      </Box>
    )
  }

  if (approvalOrder === 'sequential') {
    return (
      <Box
        sx={{
          ...circleSx,
          ...(isCurrentTurn
            ? { bgcolor: 'info.main', color: 'info.contrastText' }
            : {
                border: '1.5px solid',
                borderColor: 'text.disabled',
                color: 'text.secondary',
                opacity: isFutureStep ? 0.65 : 1,
              }),
        }}
      >
        <Typography component="span" sx={{ fontSize: 13, fontWeight: 700 }}>
          {stepNumber}
        </Typography>
      </Box>
    )
  }

  return (
    <Box
      sx={{ ...circleSx, border: '1.5px solid', borderColor: 'text.disabled' }}
    />
  )
}

function statusLabel({
  latestAction,
  approvalOrder,
  isCurrentTurn,
  notNeeded,
}: Pick<
  ApprovalApproverRowProps,
  'latestAction' | 'approvalOrder' | 'isCurrentTurn' | 'notNeeded'
>): string | null {
  // Only an approver who could still act gets a chip: a decision speaks for
  // itself, and an untracked policy has nothing to report.
  if (latestAction !== null) return null
  if (notNeeded) return 'Not needed'
  if (approvalOrder === 'sequential')
    return isCurrentTurn ? 'Pending' : 'Waiting'
  return 'Pending'
}

/**
 * One approver on a policy: their standing, who they are, and what they
 * decided. Shared by the approval card and the policy dialogs — passing no
 * `eligibility` or handlers is what leaves out the approve/reject controls.
 */
export default function ApprovalApproverRow({
  latestAction,
  positionName,
  startingPoints,
  holders,
  blocked,
  user,
  isUserPending,
  isPositionPending = false,
  isViewer,
  approvalOrder,
  stepNumber,
  isCurrentTurn,
  isFutureStep,
  notNeeded,
  eligibility,
  isActionPending = false,
  onApprove,
  onReject,
  onRejectFormOpenChange,
  canActOnBehalf = false,
  onApproveOnBehalf,
  onRejectOnBehalf,
  actedByUser,
}: ApprovalApproverRowProps) {
  const rejectForm = useRejectReasonForm({
    onReject: (reason) => onReject?.(reason),
    onOpenChange: onRejectFormOpenChange,
  })
  const proxyOverlay = useOverlay()

  const isPosition = positionName !== undefined
  const isNamePending = isPosition ? isPositionPending : !user && isUserPending
  const name = isPosition ? positionName : user?.name || 'Unknown user'
  const label = statusLabel({
    latestAction,
    approvalOrder,
    isCurrentTurn,
    notNeeded,
  })
  const action = latestAction
  const actionDetail = action
    ? `${action.type === 'approval' ? 'Approved' : 'Rejected'} ${new Date(
        action.created_at,
      ).toLocaleDateString(undefined, { dateStyle: 'short' })}`
    : null
  const actedByLabel = action?.acted_by_user_id
    ? `By ${actedByUser?.name || 'another user'} on their behalf`
    : null
  const startingPointsLabel = startingPoints?.length
    ? startingPoints.join(', ')
    : null
  const holderNamesLabel = holdersLabel(holders)

  // A blocked slot has no approval to make and nobody to show it made to —
  // never offer actions on one, even if bad data left `eligibility` set.
  const showActions = !blocked && isViewer && !!eligibility?.canAct
  const showProxyActions = !blocked && !isViewer && canActOnBehalf
  // Read the decision from this row's own `latestAction`, not from
  // `eligibility.latestActionType`. `eligibility` is computed once for the
  // whole approver entry, so on a position with several slots it can't say
  // which slot's decision it's reporting — only this row's own action can.
  const latestActionType = action?.type ?? null

  const confirmProxyAction = (
    proxyActionType: ApprovalActionType,
    proxyReason: string,
  ) => {
    if (proxyActionType === 'approval') {
      onApproveOnBehalf?.(proxyReason)
    } else {
      onRejectOnBehalf?.(proxyReason)
    }
  }

  // The viewer's turn or eligibility can change out from under an open form
  // (e.g. a refetch after someone else's decision) — close it rather than
  // leave it open, pre-filled, on a row that no longer offers actions.
  useEffect(() => {
    if (showActions || !rejectForm.isOpen) return
    rejectForm.close()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showActions])

  if (blocked) {
    const copy = blockedSlotCopy(blocked)
    return (
      <Stack
        direction="row"
        spacing={1.25}
        alignItems="flex-start"
        sx={{
          py: 0.5,
          px: 1,
          borderRadius: 1.5,
          bgcolor: (theme) => alpha(theme.palette.warning.main, 0.12),
        }}
      >
        <Box
          sx={{
            width: 24,
            height: 24,
            mt: 0.25,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 'none',
          }}
        >
          <WarningAmber sx={{ fontSize: 18, color: 'warning.main' }} />
        </Box>
        <PositionAvatar />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="body2" noWrap sx={{ lineHeight: 1.3 }}>
            {name}
          </Typography>
          {startingPointsLabel && (
            <Chip
              size="small"
              label={startingPointsLabel}
              sx={{ my: 0.5, maxWidth: '100%' }}
            />
          )}
          <Typography
            variant="caption"
            color="warning.main"
            display="block"
            sx={{ lineHeight: 1.2 }}
          >
            {copy.title}
          </Typography>
          {copy.fix && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              sx={{ lineHeight: 1.2 }}
            >
              {copy.fix}
            </Typography>
          )}
        </Box>
      </Stack>
    )
  }

  return (
    <Box onKeyDown={rejectForm.handleKeyDown}>
      <Stack
        direction="row"
        spacing={1.25}
        alignItems="center"
        sx={{
          py: 0.5,
          px: 1,
          borderRadius: 1.5,
          ...(isCurrentTurn && {
            bgcolor: (theme) => alpha(theme.palette.info.main, 0.12),
          }),
          opacity: isFutureStep ? 0.65 : 1,
        }}
      >
        <StatusIcon
          latestAction={latestAction}
          approvalOrder={approvalOrder}
          stepNumber={stepNumber}
          isCurrentTurn={isCurrentTurn}
          isFutureStep={isFutureStep}
        />
        <Box
          sx={{
            flex: 'none',
            display: 'inline-flex',
            // Matches the avatar inside it, so the turn outline hugs the
            // shape: a position is a rounded square, a person is a circle.
            borderRadius: isPosition ? 1 : '50%',
            ...(isCurrentTurn && {
              outline: '2px solid',
              outlineColor: 'info.main',
              outlineOffset: '2px',
            }),
          }}
        >
          {isPosition ? (
            <PositionAvatar loading={isNamePending} />
          ) : (
            <UserAvatar name={user?.name} loading={isNamePending} />
          )}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          {isNamePending ? (
            // Wrapped in the same Typography variant as the real name so the
            // skeleton's line box matches and the row doesn't shift on load.
            <Typography variant="body2">
              <Skeleton width={140} />
            </Typography>
          ) : (
            <Tooltip
              placement="right"
              title={
                isPosition ? (
                  <>
                    {name}
                    <br />
                    {holderNamesLabel || 'Whoever holds this position'}
                  </>
                ) : user?.email ? (
                  <>
                    {name}
                    <br />
                    {user.email}
                  </>
                ) : (
                  name
                )
              }
            >
              {/* Shrinks to the text so the tooltip anchors to the end of the
                  name, but still caps at the row width so long names truncate. */}
              <Typography
                variant="body2"
                noWrap
                sx={{
                  width: 'fit-content',
                  maxWidth: '100%',
                  // Name and date together fit the avatar's height, so an
                  // approved row is as tall as a pending one.
                  lineHeight: 1.3,
                }}
              >
                {name}
                {!isPosition && isViewer && (
                  <Typography
                    component="span"
                    variant="caption"
                    color="info.main"
                    sx={{ ml: 0.5 }}
                  >
                    (You)
                  </Typography>
                )}
              </Typography>
            </Tooltip>
          )}
          {startingPointsLabel && (
            <Chip
              size="small"
              label={startingPointsLabel}
              sx={{ my: 0.5, maxWidth: '100%' }}
            />
          )}
          {isPosition && holderNamesLabel && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              noWrap
              title={holderNamesLabel}
              sx={{ lineHeight: 1.1 }}
            >
              {holderNamesLabel}
            </Typography>
          )}
          {action && actionDetail && (
            <Tooltip placement="right" title={prettifyDate(action.created_at)}>
              <Typography
                variant="caption"
                color={
                  action.type === 'approval' ? 'success.main' : 'error.main'
                }
                display="block"
                noWrap
                sx={{ width: 'fit-content', maxWidth: '100%', lineHeight: 1.1 }}
              >
                {actionDetail}
              </Typography>
            </Tooltip>
          )}
          {actedByLabel && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              noWrap
              title={actedByLabel}
              sx={{ lineHeight: 1.1 }}
            >
              {actedByLabel}
            </Typography>
          )}
          {/* A rejection always states its reason, even when there wasn't one —
              a missing line reads as data still loading. So does an action
              someone took on the approver's behalf. An approval the approver
              made themselves shows the line only when it has a reason. */}
          {(action?.type === 'rejection' ||
            !!action?.acted_by_user_id ||
            !!action?.reason) && (
            <Typography
              variant="caption"
              color="text.secondary"
              display="block"
              noWrap
              title={action.reason || undefined}
              sx={{ lineHeight: 1.1 }}
            >
              Reason:{' '}
              {action.reason || (
                <Box component="span" sx={{ color: 'text.disabled' }}>
                  Not Specified
                </Box>
              )}
            </Typography>
          )}
        </Box>
        {/* The action cluster is the row's status when it's there — a "Pending"
            chip beside your own buttons just says what they already say. */}
        {label && !showActions && !showProxyActions && (
          <Chip size="small" label={label} sx={{ flex: 'none' }} />
        )}
        {showActions && (
          <Stack direction="row" spacing={1} sx={{ flex: 'none' }}>
            {rejectForm.isOpen ? (
              <Button
                variant="text"
                disabled={isActionPending}
                onClick={rejectForm.close}
              >
                Cancel
              </Button>
            ) : (
              <>
                {latestActionType !== 'rejection' && (
                  <Button
                    color="error"
                    variant={latestActionType ? 'outlined' : 'contained'}
                    disabled={isActionPending}
                    onClick={rejectForm.open}
                    title={
                      latestActionType === 'approval'
                        ? 'Change your decision to rejected'
                        : undefined
                    }
                  >
                    Reject
                  </Button>
                )}
                {latestActionType !== 'approval' && (
                  <Button
                    color="success"
                    variant={latestActionType ? 'outlined' : 'contained'}
                    disabled={isActionPending}
                    onClick={onApprove}
                    title={
                      latestActionType === 'rejection'
                        ? 'Change your decision to approved'
                        : undefined
                    }
                  >
                    Approve
                  </Button>
                )}
              </>
            )}
          </Stack>
        )}
        {showProxyActions && (
          <Tooltip title={`Override on behalf of ${name}`}>
            {/* The span keeps the tooltip working while the button is disabled. */}
            <span>
              <IconButton
                size="small"
                aria-label={`Override on behalf of ${name}`}
                disabled={isActionPending}
                onClick={proxyOverlay.open}
                sx={{
                  flex: 'none',
                  color: 'text.secondary',
                  '&:hover': {
                    color: 'primary.main',
                    bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                  },
                }}
              >
                <HowToRegOutlined fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Stack>

      {showProxyActions && (
        <ProxyActionDialog
          overlay={proxyOverlay}
          approverName={name}
          approverEmail={user?.email}
          currentActionType={latestActionType}
          onConfirm={confirmProxyAction}
        />
      )}

      {/* No right padding: the row above puts its px inside its own
          background, so matching it means going flush right. */}
      {showActions && rejectForm.isOpen && (
        <RejectReasonForm
          reason={rejectForm.reason}
          onReasonChange={rejectForm.setReason}
          onConfirm={rejectForm.confirm}
          disabled={isActionPending}
          sx={{ pl: 6, pt: 1, pb: 1 }}
        />
      )}
    </Box>
  )
}
